import {
  FBA_INVENTORY_FRESHNESS_MS,
  getProductListingSelectionState,
  type ProductCatalogState,
  type ProductListing,
  type ProductListingGroup,
  type ProductListingSelectionState,
  type ResolveProductListingsResponse,
  syncRunChangedEventSchema,
} from "@adpilot/shared";
import type { CampaignCreationInvalidProduct } from "@/lib/campaign-creation-session";
import type { SelectedProduct } from "@/lib/campaign-creation-state";

type Translate = (key: string) => string;

export async function selectedSkuSetFingerprint(values: string[]): Promise<string> {
  const normalized = [...new Set(values)].sort().join("\u0000");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(normalized));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

const AMAZON_ORIGIN_BY_MARKETPLACE_ID: Record<string, string> = {
  A1AM78C64UM0Y8: "https://www.amazon.com.mx",
  A1C3SOZRARQ6R3: "https://www.amazon.pl",
  A1F83G8C2ARO7P: "https://www.amazon.co.uk",
  A1PA6795UKMFR9: "https://www.amazon.de",
  A1RKKUPIHCS9HS: "https://www.amazon.es",
  A1VC38T7YXB528: "https://www.amazon.co.jp",
  A13V1IB3VIYZZH: "https://www.amazon.fr",
  A17E79C6D8DWNP: "https://www.amazon.sa",
  A19VAU5U5O7RUS: "https://www.amazon.sg",
  A21TJRUUN4KGV: "https://www.amazon.in",
  A2EUQ1WTGCTBG2: "https://www.amazon.ca",
  A2NODRKZP88ZB9: "https://www.amazon.se",
  A2Q3Y263D00KWC: "https://www.amazon.com.br",
  A2VIGQ35RCS4UG: "https://www.amazon.ae",
  A33AVAJ2PDY3EV: "https://www.amazon.com.tr",
  A39IBJ37TRP1C6: "https://www.amazon.com.au",
  AMEN7PMS3EDWL: "https://www.amazon.com.be",
  APJ6JRA9NG5V4: "https://www.amazon.it",
  ARBP9OOSHTCHU: "https://www.amazon.eg",
  ATVPDKIKX0DER: "https://www.amazon.com",
};

export function amazonProductUrl(asin: string, marketplaceId: string): string {
  const origin = AMAZON_ORIGIN_BY_MARKETPLACE_ID[marketplaceId] ?? "https://www.amazon.com";
  return `${origin}/dp/${encodeURIComponent(asin)}`;
}

export function formatListingPrice(
  amount: string | null,
  currency: string | null,
  locale: string,
): string | null {
  if (!amount) return null;
  const value = Number(amount);
  if (!currency || !Number.isFinite(value)) return [currency, amount].filter(Boolean).join(" ");
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${amount}`;
  }
}

export function selectedProductFromListing(listing: {
  listingKey: string;
  sku: string | null;
  asin: string;
  variationGroupKey: string;
  title: string | null;
  imageUrl: string | null;
  eligibility?: ProductListing["eligibility"];
  eligibilityReasons?: ProductListing["eligibilityReasons"];
  eligibilityCheckedAt?: ProductListing["eligibilityCheckedAt"];
  eligibilitySource?: ProductListing["eligibilitySource"];
}): SelectedProduct {
  return {
    listingKey: listing.listingKey,
    sku: listing.sku,
    asin: listing.asin,
    variationGroupKey: listing.variationGroupKey,
    title: listing.title,
    imageUrl: listing.imageUrl,
    ...(listing.eligibility && listing.eligibility !== "unknown"
      ? { eligibility: listing.eligibility }
      : {}),
    ...(listing.eligibilityReasons ? { eligibilityReasons: listing.eligibilityReasons } : {}),
    ...(listing.eligibilityCheckedAt ? { eligibilityCheckedAt: listing.eligibilityCheckedAt } : {}),
    ...(listing.eligibilitySource ? { eligibilitySource: listing.eligibilitySource } : {}),
  };
}

const KNOWN_ELIGIBILITY_REASONS = new Set([
  "ADULT_PRODUCT",
  "CLOSED_CATEGORY",
  "INELIGIBLE_CONDITION",
  "INELIGIBLE_OFFER",
  "INELIGIBLE_PRODUCT_COST",
  "LISTING_SUPRESSED",
  "MISSING_IMAGE",
  "MISSING_TITLE",
  "NOT_IN_BUYBOX",
  "OUT_OF_STOCK",
  "RESTRICTED_CATEGORY",
  "VARIATION_PARENT",
]);

export function eligibilityExplanation(
  listing: Pick<ProductListing, "eligibilityReasons">,
  translate: Translate,
): string | undefined {
  const reasons = listing.eligibilityReasons?.map(({ message, name }) =>
    KNOWN_ELIGIBILITY_REASONS.has(name)
      ? translate(`products.adEligibilityReasons.${name}`)
      : message || translate("products.adEligibility.reasonUnavailable"),
  );
  return reasons?.length ? [...new Set(reasons)].join(" · ") : undefined;
}

export function invalidProductExplanations(
  invalid: CampaignCreationInvalidProduct,
  translate: Translate,
): string[] {
  if (invalid.reason === "ineligible") {
    // Amazon messages are requested in a stable diagnostic language and shared
    // in the Shop-level eligibility cache. User-facing copy must follow the UI
    // locale by translating stable reason codes instead of rendering that text.
    const explanation = eligibilityExplanation(
      { eligibilityReasons: invalid.eligibilityReasons },
      translate,
    );
    return explanation
      ? explanation.split(" · ")
      : [translate("products.invalidReasons.ineligible")];
  }
  return [invalid.message ?? translate(`products.invalidReasons.${invalid.reason}`)];
}

export function matchesProductIssue(
  issue: Pick<CampaignCreationInvalidProduct, "sku" | "asin">,
  product: { sku: string | null; asin: string },
): boolean {
  return issue.sku ? issue.sku === product.sku : issue.asin === product.asin;
}

const UNIFIED_TABLE_COLUMNS = [
  "select",
  "product",
  "type",
  "status",
  "price",
  "inventory",
  "actions",
];

/** Columns of the unified product table; a vendor catalog has no stock evidence (#673). */
export function unifiedTableColumns(vendor: boolean): string[] {
  return vendor
    ? UNIFIED_TABLE_COLUMNS.filter((column) => column !== "inventory")
    : UNIFIED_TABLE_COLUMNS;
}

export function unifiedTableColumnCount(vendor: boolean): number {
  return unifiedTableColumns(vendor).length;
}

/** A vendor (1P) catalog is keyed by ASIN: no SKU, fulfillment or stock evidence (#673). */
export function isVendorCatalog(catalog: Pick<ProductCatalogState, "accountType"> | undefined) {
  return catalog?.accountType === "vendor";
}

/** Vendors have no inventory evidence, so the stock gate never applies to them. */
export function effectiveInventoryGate(
  inventory: ProductInventoryGate,
  vendor: boolean,
): ProductInventoryGate {
  return vendor ? "ignore" : inventory;
}

export interface InventoryListing {
  isActive?: boolean;
  fulfillmentChannel?: "FBA" | "FBM" | "MIXED" | null;
  quantity?: number | null;
  fbaFulfillableQuantity?: number | null;
  fbaInventorySyncedAt?: string | null;
  effectiveInventoryQuantity?: number | null;
  inventorySources?: ProductListing["inventorySources"];
}

export function displayedInventoryQuantity(listing: InventoryListing): number | null {
  if ("effectiveInventoryQuantity" in listing) return listing.effectiveInventoryQuantity ?? null;
  if (listing.isActive === false) return listing.quantity ?? null;
  if (listing.fulfillmentChannel !== "FBA" && listing.fulfillmentChannel !== "MIXED")
    return listing.quantity ?? null;
  if (listing.fbaFulfillableQuantity == null || !listing.fbaInventorySyncedAt)
    return listing.quantity ?? null;
  const age = Date.now() - Date.parse(listing.fbaInventorySyncedAt);
  return age >= 0 && age < FBA_INVENTORY_FRESHNESS_MS
    ? listing.fbaFulfillableQuantity
    : (listing.quantity ?? null);
}

/**
 * Smart Creation and manual creation only accept SKUs with known positive stock.
 * Template portfolios never gated on stock (sellers build them ahead of restocks),
 * so they pass `ignore` and still see the quantity.
 */
export type ProductInventoryGate = "require_known_stock" | "ignore";

type SelectionStateListing = Parameters<typeof getProductListingSelectionState>[0];

export function listingBlockedReason(
  listing: SelectionStateListing,
  inventory: ProductInventoryGate = "require_known_stock",
): ProductListingSelectionState["blockedReason"] {
  const { blockedReason } = getProductListingSelectionState(listing);
  return inventory === "ignore" &&
    (blockedReason === "out_of_stock" || blockedReason === "inventory_unknown")
    ? null
    : blockedReason;
}

export function cannotAddListing(
  listing: ProductListing,
  adProduct?: "sp",
  inventory: ProductInventoryGate = "require_known_stock",
): boolean {
  return (
    Boolean(listingBlockedReason(listing, inventory)) ||
    (adProduct === "sp" && listing.eligibility === "unknown")
  );
}

export function isExpandableGroup(group: ProductListingGroup): boolean {
  return group.activeSkuCount > 1;
}

/**
 * Only an unknown account type blocks completion (#673): a vendor's Ads-only
 * catalog is its real catalog, and a seller's partial one still lists real rows.
 */
export function productCatalogBlocksCompletion(catalog?: ProductCatalogState): boolean {
  return !catalog || catalog.completeness === "account-type-unknown";
}

export function isProductSyncEvent(event: Event, runId?: string): boolean {
  if (!(event instanceof MessageEvent)) return false;
  try {
    const parsed = syncRunChangedEventSchema.safeParse(JSON.parse(String(event.data)));
    return (
      parsed.success &&
      parsed.data.jobType === "sync:products" &&
      (Boolean(runId) || parsed.data.status !== "running") &&
      (!runId || parsed.data.runId === runId)
    );
  } catch {
    return false;
  }
}

function resolvedListing(response: ResolveProductListingsResponse, listingKey: string) {
  const result = response.results.find(({ identifier }) => identifier === listingKey);
  return result && "listing" in result ? result.listing : undefined;
}

/** Issues shown while the background validation query re-checks the saved selection. */
export function selectionValidationIssues(
  selected: SelectedProduct[],
  response: ResolveProductListingsResponse,
  adProduct: "sp" | undefined,
  translate: Translate,
): CampaignCreationInvalidProduct[] {
  return selected.flatMap((product): CampaignCreationInvalidProduct[] => {
    const listing = resolvedListing(response, product.listingKey);
    const reason =
      !listing || listing.listingKey !== product.listingKey
        ? "missing_sku"
        : !listing.isActive
          ? "not_active"
          : listing.asin !== product.asin
            ? "asin_mismatch"
            : listing.listingRole === "parent"
              ? "parent"
              : listing.eligibility === "ineligible"
                ? "ineligible"
                : adProduct === "sp" && listing.eligibilityError === "not_returned"
                  ? "not_returned"
                  : null;
    return reason
      ? [
          {
            sku: product.sku,
            asin: product.asin,
            reason,
            ...(reason === "ineligible" && listing
              ? {
                  message: eligibilityExplanation(listing, translate),
                  eligibilityReasons: listing.eligibilityReasons,
                }
              : {}),
          },
        ]
      : [];
  });
}

/** Issues persisted after the user explicitly re-checks the selection, including stock state. */
export function retriedSelectionIssues(
  requested: SelectedProduct[],
  response: ResolveProductListingsResponse,
  adProduct: "sp" | undefined,
  inventory: ProductInventoryGate = "require_known_stock",
): CampaignCreationInvalidProduct[] {
  return requested.flatMap((product): CampaignCreationInvalidProduct[] => {
    const listing = resolvedListing(response, product.listingKey);
    const inventoryReason = listing ? listingBlockedReason(listing, inventory) : null;
    const reason =
      !listing || listing.listingKey !== product.listingKey
        ? "missing_sku"
        : !listing.isActive
          ? "not_active"
          : listing.asin !== product.asin
            ? "asin_mismatch"
            : listing.listingRole === "parent"
              ? "parent"
              : inventoryReason === "out_of_stock" || inventoryReason === "inventory_unknown"
                ? inventoryReason
                : listing.eligibility === "ineligible"
                  ? "ineligible"
                  : adProduct === "sp" && listing.eligibility === "unknown"
                    ? "not_returned"
                    : null;
    return reason
      ? [
          {
            sku: product.sku,
            asin: product.asin,
            reason,
            ...(reason === "ineligible" && listing
              ? { eligibilityReasons: listing.eligibilityReasons }
              : {}),
          },
        ]
      : [];
  });
}
